import { SpaceVertical } from '@looker/components';
import { BIIncludeExclude, BISearchBar, type IncludeExcludeState, useToast } from '@pulsepointinc/bi-components';
import { DsTab, DsTabBar } from '@pulsepointinc/design-system-react';
import debounce from 'lodash/debounce';
import type React from 'react';
import { type FC, useCallback, useMemo, useState } from 'react';
import { MIN_SEARCH_PHRASE_LENGTH, SEARCH_DEBOUNCE_TIMEOUT } from '../../../../../../helpers';
import type { ICustomFilter, SuggestionItem } from '../../../../../../helpers/helpers';
import { useFilters } from '../../../../../../hooks/useFilters';
import { logEvent } from '../../../../../../lib/eventsLogger';
import type { Filter } from '../../../../../../stores/filters';
import { FilterSearchNoResults } from '../../FilterSearchNoResults/Index';
import { AwaitingListsAccordion } from '../NpiListName/AwaitingListsAccordion';
import { useAliasedFilterState } from '../NpiListName/hooks/useAliasedFilterState';
import { BulkUpdateSection } from './BulkUpdateSection';
import { IncludeExcludeListLoader } from './IncludeExcludeListLoader';
import { SelectedOnlySection } from './SelectedOnlySection';
import type { IncludeExcludeTab } from './types';

interface FilterProps extends ICustomFilter {
  filterName: string;
  label?: string;
  onMultiFilterChange?: (filter: Filter) => void;
  groupedSuggestions: Record<string, SuggestionItem[]>;
  urlAnchor?: (category: string) => React.ReactNode;
  loading?: boolean;
  onSearchPhraseChange?: (searchPhrase: string) => void;
  awaitingData?: Record<string, { listName: string; listAlias?: string; url?: string }[]>;
}

export const GroupedIncludeExcludeFilter: FC<FilterProps> = ({
  filterName,
  label = '',
  onExpressionTypeChange,
  onMultiFilterChange,
  groupedSuggestions,
  awaitingData,
  urlAnchor,
  loading = false,
  onSearchPhraseChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { showError } = useToast();
  const [tab, setTab] = useState<IncludeExcludeTab>('all');

  const { filters } = useFilters();
  const filtersList = useMemo(() => (filters ? (Object.values(filters) as Filter[]) : []), [filters]);
  const filter = filtersList.find((filter) => filter.filterName === filterName);

  const filterListExpression = filter ? filter.expression : '';
  const { filterState, mode, handleChange } = useAliasedFilterState({
    expression: filterListExpression,
    advertiserID: filters?.pseudo_advertiser_id_filter?.expression,
    suggestions: groupedSuggestions,
  });

  const allSuggestions = useMemo(() => {
    return Object.values(groupedSuggestions).reduce<string[]>(
      (acc, list) => acc.concat(list.map((item) => item.listName)),
      [],
    );
  }, [groupedSuggestions]);

  const handleFilterChange = useCallback(
    (newState: IncludeExcludeState) => {
      const result = handleChange(newState);
      if (typeof result === 'string') {
        logEvent('NPI_LIST_SELECTION_ERROR', {
          filterName,
          missingItems: result,
        });
        showError('An error occurred while updating filters.');
        return;
      }

      const { expression, expressionType, mode } = result;
      if (mode) onExpressionTypeChange?.(expressionType);

      onMultiFilterChange?.({
        filterName,
        expression,
        enabled: true,
        expressionType,
      });
    },
    [filterName, handleChange, onExpressionTypeChange, onMultiFilterChange, showError],
  );

  const onSearch = useMemo(
    () =>
      debounce((value: string) => {
        const actualValue = value.length >= MIN_SEARCH_PHRASE_LENGTH ? value : '';
        setSearchTerm(actualValue);
        onSearchPhraseChange?.(actualValue);
      }, SEARCH_DEBOUNCE_TIMEOUT),
    [onSearchPhraseChange],
  );

  const filteredGroupedCategories = useMemo(() => {
    return Object.fromEntries(
      Object.entries(groupedSuggestions).map(([group, items]) => [group, items.map((item) => item.listName)]),
    );
  }, [groupedSuggestions]);

  const filteredAwaitingData = useMemo(() => {
    if (!awaitingData) return {};
    return awaitingData;
  }, [awaitingData]);

  const selectedCategories = useMemo(() => {
    return Object.keys(filterState).filter((category) => filterState[category]?.check || filterState[category]?.block);
  }, [filterState]);

  const displayNoResults = useMemo(() => {
    if (!searchTerm || loading) {
      return false;
    }

    const hasRegularSuggestions = Object.keys(filteredGroupedCategories).length > 0;
    const hasAwaitingData = Object.keys(filteredAwaitingData).length > 0;

    return !hasRegularSuggestions && !hasAwaitingData;
  }, [searchTerm, filteredGroupedCategories, filteredAwaitingData, loading]);

  return (
    <SpaceVertical gap="none" height="100%">
      <DsTabBar layout="panel" value={tab} onValueChange={(e) => setTab(e.target.value as IncludeExcludeTab)}>
        <DsTab value="all" label={`All ${label}`} />
        <DsTab value="selected" label={`Selected Only (${selectedCategories.length})`} />
      </DsTabBar>
      {tab === 'all' && (
        <SpaceVertical flex={1} paddingX="u6" paddingTop="u3">
          <BISearchBar defaultValue={searchTerm} onSearchChange={onSearch} placeholder="Search" showClearButton />
          {searchTerm && !loading && !displayNoResults && (
            <BulkUpdateSection
              filterState={filterState}
              handleFilterChange={handleFilterChange}
              categories={allSuggestions}
              mode={mode}
            />
          )}
          {loading ? (
            <IncludeExcludeListLoader />
          ) : displayNoResults ? (
            <FilterSearchNoResults />
          ) : (
            <SpaceVertical flex={1} gap="u3">
              {awaitingData && Object.keys(filteredAwaitingData || {}).length > 0 && (
                <AwaitingListsAccordion awaitingData={filteredAwaitingData} />
              )}
              <BIIncludeExclude
                mode={mode}
                initialState={filterState}
                groupedCategories={filteredGroupedCategories}
                categories={selectedCategories}
                onToggleChange={handleFilterChange}
                after={urlAnchor}
              />
            </SpaceVertical>
          )}
        </SpaceVertical>
      )}
      {tab === 'selected' && (
        <SelectedOnlySection
          mode={mode}
          state={filterState}
          selected={selectedCategories}
          onChange={handleFilterChange}
        />
      )}
    </SpaceVertical>
  );
};
